import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { HPC_COLORS } from "./hpcTheme";
import { fetchFamilyQuote, formatLkr } from "./hpcMembershipPricing";

const CATEGORY_LABELS = { adult: "Adult (18+)", student: "Student (under 18)" };

export const familyCategoryLabel = (category) => CATEGORY_LABELS[category] || category || "—";

const formatPercent = (value) => (value ? `${Number(value)}%` : "Standard");

const headerSx = {
  fontWeight: 700,
  color: HPC_COLORS.silver,
  borderBottom: "1px solid rgba(245, 166, 35, 0.2)",
  whiteSpace: "nowrap",
};

/**
 * Read-only table for family prices. ``rows`` use the server's field names
 * (full_name, position, price_category, list_price, discount_percent, net_fee).
 */
export const FamilyBreakdownTable = ({ rows, totals, showAdmission = true }) => (
  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
    <Table size="small" sx={{ minWidth: 560 }}>
      <TableHead>
        <TableRow>
          {[
            "Member",
            "Position",
            "Price type",
            "Price",
            "Discount",
            "Net",
            ...(showAdmission ? ["Admission"] : []),
          ].map((col) => (
            <TableCell key={col} sx={headerSx}>
              {col}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => (
          <TableRow key={row.membership_id || `${row.position}-${index}`}>
            <TableCell sx={{ fontWeight: 600 }}>{row.full_name || `Member ${index + 1}`}</TableCell>
            <TableCell>{row.position ?? "—"}</TableCell>
            <TableCell>{familyCategoryLabel(row.price_category)}</TableCell>
            <TableCell>{row.list_price != null ? formatLkr(row.list_price) : "—"}</TableCell>
            <TableCell>{formatPercent(row.discount_percent)}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>
              {row.net_fee != null ? formatLkr(row.net_fee) : "—"}
            </TableCell>
            {showAdmission && (
              <TableCell>{row.admission_fee ? `+ ${formatLkr(row.admission_fee)}` : "—"}</TableCell>
            )}
          </TableRow>
        ))}
        {totals.map(([label, amount]) => (
          <TableRow key={label}>
            <TableCell colSpan={showAdmission ? 6 : 5} align="right" sx={{ fontWeight: 700 }}>
              {label}
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: HPC_COLORS.hpuOrange }}>
              {formatLkr(amount)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

/**
 * Live Family price preview. The price comes from the server quote endpoint;
 * ``members`` is the main member followed by the other family members.
 */
const FamilyPriceTable = ({ membershipType, members, onQuote }) => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const onQuoteRef = useRef(onQuote);
  onQuoteRef.current = onQuote;

  const missingDob = members.some((m) => !m.dateOfBirth);
  const requestKey = JSON.stringify({
    membershipType,
    members: members.map((m) => [m.fullName || "", m.dateOfBirth || ""]),
  });

  useEffect(() => {
    if (!membershipType || missingDob || members.length === 0) {
      setQuote(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const timer = setTimeout(() => {
      fetchFamilyQuote(membershipType, members)
        .then((data) => {
          if (cancelled) return;
          setQuote(data);
          if (onQuoteRef.current) onQuoteRef.current(data);
        })
        .catch((err) => {
          if (cancelled) return;
          setQuote(null);
          setError(err.response?.data?.error || "Could not calculate the family price.");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // requestKey captures every input the quote depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey]);

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, color: HPC_COLORS.silver }}>
        Family price
      </Typography>
      {missingDob && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Enter the date of birth for every family member to see the price.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {loading && !quote && <CircularProgress size={22} />}
      {quote && !missingDob && (
        <FamilyBreakdownTable
          rows={quote.members}
          totals={[
            ["Membership total", quote.membership_total],
            ["Admission total", quote.admission_total],
            ["Family total", quote.total],
          ]}
        />
      )}
    </Box>
  );
};

export default FamilyPriceTable;
